import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { NumberField, SelectField, SettingsCard, SwitchField } from '@/features/store-settings';

import type { ChannelOverrideController } from '../../model/channelOverrideController';
import type { FieldScope } from '../../model/channelOverrideController';
import { minutesEquivalent } from '../../model/format';
import { BEHAVIOUR_ADVANCED_FIELDS } from '../../model/formModel';
import { formatSettingValue } from '../../model/provenance';
import type {
  ChatbotChannel,
  ChatbotDictionaries,
  ChatbotGreetingFlow,
  ChatbotLanguagePolicy,
  ChatbotSettingsFields,
} from '../../model/types';
import { outOfRange } from '../../model/validation';
import { CommandsField } from './CommandsField';
import { ChannelScopeTabs } from './ChannelScopeTabs';
import type { ChannelScope } from './ChannelScopeTabs';
import { ResetScopeDialog } from './ResetScopeDialog';
import { ScopedFieldLabel } from './ScopedFieldLabel';

export interface BehaviourCardProps {
  scope: ChannelScope;
  onScopeChange: (scope: ChannelScope) => void;
  controller: ChannelOverrideController;
  canManage: boolean;
  languageRegistrySummary?: string;
  onValidityChange: (valid: boolean) => void;
  dictionaries: ChatbotDictionaries | undefined;
  dictionariesLoading: boolean;
  dictionariesError: boolean;
  onRetryDictionaries: () => void;
}

function equivalentText(
  minutes: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string | null {
  const equivalent = minutesEquivalent(minutes);
  if (!equivalent || equivalent.unit === 'minutes') return null;
  return t('units.equals', { value: `${equivalent.value} ${t(`units.${equivalent.unit}`)}` });
}

/** The Behaviour card (design §8, extended §17.1/§17.3.1): greeting, commands, language, tidy-up,
 * plus the epic-003 "Episodes & replies" and collapsed "Advanced delivery & housekeeping" groups. */
export function BehaviourCard({
  scope,
  onScopeChange,
  controller,
  canManage,
  languageRegistrySummary,
  onValidityChange,
  dictionaries,
  dictionariesLoading,
  dictionariesError,
  onRetryDictionaries,
}: BehaviourCardProps) {
  const { t } = useTranslation('chatbot');
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [confirmDefaultsOpen, setConfirmDefaultsOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const greetingFlow = controller.field('greetingFlow');
  const commands = controller.field('enabledCommands');
  const languagePolicy = controller.field('languagePolicy');
  const closeAfterIdleDays = controller.field('closeAfterIdleDays');
  const retentionDays = controller.field('retentionDays');
  const episodeIdleTimeoutMinutes = controller.field('episodeIdleTimeoutMinutes');
  const idleGraceMinutes = controller.field('idleGraceMinutes');
  const quoteInboundInReplies = controller.field('quoteInboundInReplies');
  const identityNegativeCacheTtlHours = controller.field('identityNegativeCacheTtlHours');
  const unsupportedReplyCooldownSeconds = controller.field('unsupportedReplyCooldownSeconds');
  const deliveryMaxRedispatch = controller.field('deliveryMaxRedispatch');
  const handoffEnabled = controller.field('handoffEnabled').value;

  const formatCtx = { t, dictionaries };
  function hintFor<K extends keyof ChatbotSettingsFields>(field: FieldScope<K>, key: K): ReactNode {
    if (
      scope !== 'GENERAL' ||
      field.source !== 'GENERAL' ||
      field.platformDefaultValue === undefined
    ) {
      return null;
    }
    const text = field.sameAsDefault
      ? t('provenance.sameAsPlatformDefault')
      : t('provenance.platformDefaultValue', {
          value: formatSettingValue(key, field.platformDefaultValue, formatCtx),
        });
    return (
      <Typography component="span" display="block" variant="caption" color="text.secondary">
        {text}
      </Typography>
    );
  }
  function withHint<K extends keyof ChatbotSettingsFields>(
    base: string,
    field: FieldScope<K>,
    key: K,
  ): ReactNode {
    const hint = hintFor(field, key);
    return hint ? (
      <>
        {base}
        {hint}
      </>
    ) : (
      base
    );
  }
  function scopedLabel<K extends keyof ChatbotSettingsFields>(
    label: string,
    field: FieldScope<K>,
  ): ReactNode {
    return (
      <ScopedFieldLabel
        label={label}
        scope={scope}
        source={field.source}
        pending={field.pending}
        onUseGeneral={field.onUseGeneral}
        onCustomiseRow={field.onCustomiseRow}
        onUseDefault={field.onUseDefault}
        onKeepYours={field.onKeepYours}
      />
    );
  }

  // Plain-text labels — used both as the accessible name (`ariaLabel`) and inside the chrome above.
  const greetingFlowLabel = t('behaviour.greetingFlow.label');
  const commandsLabel = t('behaviour.commands.label');
  const languageLabel = t('behaviour.language.label');
  const closeAfterIdleLabel = t('behaviour.closeAfterIdle.label');
  const retentionLabel = t('behaviour.retention.label');
  const episodeIdleTimeoutLabel = t('behaviour.episodeIdleTimeout.label');
  const idleGraceLabel = t('behaviour.idleGrace.label');
  const quoteInboundLabel = t('behaviour.quoteInbound.label');
  const identityNegativeCacheLabel = t('behaviour.identityNegativeCache.label');
  const unsupportedCooldownLabel = t('behaviour.unsupportedCooldown.label');
  const deliveryMaxRedispatchLabel = t('behaviour.deliveryMaxRedispatch.label');

  const idleGraceError = outOfRange(idleGraceMinutes.value, 1, 1440)
    ? t('validation.range', { min: 1, max: 1440 })
    : episodeIdleTimeoutMinutes.value !== null &&
        idleGraceMinutes.value !== null &&
        idleGraceMinutes.value >= episodeIdleTimeoutMinutes.value
      ? t('behaviour.idleGrace.mustBeShorter')
      : null;
  const episodeIdleError = outOfRange(episodeIdleTimeoutMinutes.value, 60, 43200)
    ? t('validation.range', { min: 60, max: 43200 })
    : null;
  const identityNegativeCacheError = outOfRange(identityNegativeCacheTtlHours.value, 1, 168)
    ? t('validation.range', { min: 1, max: 168 })
    : null;
  const unsupportedCooldownError = outOfRange(unsupportedReplyCooldownSeconds.value, 0, 3600)
    ? t('validation.range', { min: 0, max: 3600 })
    : null;
  const deliveryMaxRedispatchError = outOfRange(deliveryMaxRedispatch.value, 0, 20)
    ? t('validation.range', { min: 0, max: 20 })
    : null;
  const closeAfterIdleError = outOfRange(closeAfterIdleDays.value, 1, 365)
    ? t('validation.range', { min: 1, max: 365 })
    : null;
  const retentionError = outOfRange(retentionDays.value, 30, 730)
    ? t('validation.range', { min: 30, max: 730 })
    : null;

  const advancedHasError = Boolean(
    identityNegativeCacheError || unsupportedCooldownError || deliveryMaxRedispatchError,
  );
  const advancedDirty = BEHAVIOUR_ADVANCED_FIELDS.some(
    (field) => controller.field(field).source === 'OVERRIDE',
  );
  const shouldExpandAdvanced = advancedHasError || advancedDirty;

  useEffect(() => {
    if (shouldExpandAdvanced) setAdvancedOpen(true);
  }, [shouldExpandAdvanced]);

  useEffect(() => {
    onValidityChange(
      !idleGraceError &&
        !episodeIdleError &&
        !identityNegativeCacheError &&
        !unsupportedCooldownError &&
        !deliveryMaxRedispatchError &&
        !closeAfterIdleError &&
        !retentionError,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    idleGraceError,
    episodeIdleError,
    identityNegativeCacheError,
    unsupportedCooldownError,
    deliveryMaxRedispatchError,
    closeAfterIdleError,
    retentionError,
  ]);

  const overridesByChannel: Record<ChatbotChannel, boolean> = {
    TELEGRAM: controller.hasOverride('TELEGRAM'),
    MAX: controller.hasOverride('MAX'),
  };

  const handleReset = () => {
    if (controller.activeChannelCardDirty) {
      setConfirmResetOpen(true);
    } else {
      controller.resetCard();
    }
  };

  const activeChannel: ChatbotChannel | null = scope === 'GENERAL' ? null : scope;
  const resettableCount = controller.resettableGeneralFields.length;

  return (
    <SettingsCard
      title={t('behaviour.title')}
      description={t('behaviour.description')}
      icon={<TuneRoundedIcon />}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          {scope === 'GENERAL' && canManage && resettableCount > 0 ? (
            <Button size="small" onClick={() => setConfirmDefaultsOpen(true)}>
              {t('provenance.resetCard')}
            </Button>
          ) : null}
          <ChannelScopeTabs
            value={scope}
            onChange={onScopeChange}
            overrides={overridesByChannel}
            cardTitle={t('behaviour.title')}
          />
        </Stack>
      }
    >
      {activeChannel ? (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          {controller.activeChannelHasOverride ? (
            <Button size="small" onClick={handleReset} disabled={!canManage}>
              {t('scope.resetToGeneral')}
            </Button>
          ) : (
            <Button size="small" onClick={controller.customiseCard} disabled={!canManage}>
              {t('scope.customiseFor', { channel: t(`scope.${activeChannel.toLowerCase()}`) })}
            </Button>
          )}
        </Stack>
      ) : null}

      <SelectField
        label={scopedLabel(greetingFlowLabel, greetingFlow)}
        ariaLabel={greetingFlowLabel}
        description={withHint(
          t('behaviour.greetingFlow.description'),
          greetingFlow,
          'greetingFlow',
        )}
        value={greetingFlow.value}
        options={[
          { value: 'SIMPLE', label: t('behaviour.greetingFlow.options.simple') },
          {
            value: 'REGISTRATION_FIRST',
            label: t('behaviour.greetingFlow.options.registrationFirst'),
          },
        ]}
        onChange={(value) => greetingFlow.onChange(value as ChatbotGreetingFlow)}
        disabled={greetingFlow.disabled}
      />

      <CommandsField
        label={scopedLabel(commandsLabel, commands)}
        description={withHint(t('behaviour.commands.description'), commands, 'enabledCommands')}
        ariaLabel={commandsLabel}
        value={commands.value ?? []}
        onChange={commands.onChange}
        disabled={commands.disabled}
        commands={dictionaries?.commands}
        isLoading={dictionariesLoading}
        isError={dictionariesError}
        onRetry={onRetryDictionaries}
        handoffEnabled={handoffEnabled}
      />

      <SelectField
        label={scopedLabel(languageLabel, languagePolicy)}
        ariaLabel={languageLabel}
        description={
          languageRegistrySummary ? (
            <>
              {withHint(t('behaviour.language.description'), languagePolicy, 'languagePolicy')}{' '}
              {t('behaviour.language.registrySummary', { languages: languageRegistrySummary })}
            </>
          ) : (
            withHint(t('behaviour.language.description'), languagePolicy, 'languagePolicy')
          )
        }
        value={languagePolicy.value}
        options={[
          { value: 'CUSTOMER', label: t('behaviour.language.options.customer') },
          { value: 'TENANT_DEFAULT', label: t('behaviour.language.options.tenantDefault') },
        ]}
        onChange={(value) => languagePolicy.onChange(value as ChatbotLanguagePolicy)}
        disabled={languagePolicy.disabled}
      />

      <NumberField
        label={scopedLabel(closeAfterIdleLabel, closeAfterIdleDays)}
        ariaLabel={closeAfterIdleLabel}
        description={withHint(
          t('behaviour.closeAfterIdle.description'),
          closeAfterIdleDays,
          'closeAfterIdleDays',
        )}
        value={closeAfterIdleDays.value}
        onChange={(value) => closeAfterIdleDays.onChange(value ?? 1)}
        disabled={closeAfterIdleDays.disabled}
        min={1}
        max={365}
        suffix={t('behaviour.closeAfterIdle.suffix')}
        error={Boolean(closeAfterIdleError)}
        helperText={closeAfterIdleError}
      />
      <NumberField
        label={scopedLabel(retentionLabel, retentionDays)}
        ariaLabel={retentionLabel}
        description={
          <>
            {t('behaviour.retention.description')} {t('behaviour.retention.helper')}
            {hintFor(retentionDays, 'retentionDays')}
          </>
        }
        value={retentionDays.value}
        onChange={(value) => retentionDays.onChange(value ?? 30)}
        disabled={retentionDays.disabled}
        min={30}
        max={730}
        suffix={t('behaviour.retention.suffix')}
        error={Boolean(retentionError)}
        helperText={retentionError}
      />

      <Divider sx={{ my: 2 }} />
      <Typography
        variant="overline"
        color="text.secondary"
        component="h3"
        sx={{ display: 'block' }}
      >
        {t('behaviour.episodes.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {t('behaviour.episodes.description')}
      </Typography>

      <NumberField
        label={scopedLabel(episodeIdleTimeoutLabel, episodeIdleTimeoutMinutes)}
        ariaLabel={episodeIdleTimeoutLabel}
        description={withHint(
          t('behaviour.episodeIdleTimeout.description'),
          episodeIdleTimeoutMinutes,
          'episodeIdleTimeoutMinutes',
        )}
        value={episodeIdleTimeoutMinutes.value}
        onChange={(value) => episodeIdleTimeoutMinutes.onChange(value ?? 1440)}
        disabled={episodeIdleTimeoutMinutes.disabled}
        min={60}
        max={43200}
        step={30}
        suffix={
          episodeIdleTimeoutMinutes.value !== null
            ? `${t('units.minutes')} ${equivalentText(episodeIdleTimeoutMinutes.value, t) ?? ''}`.trim()
            : t('units.minutes')
        }
        error={Boolean(episodeIdleError)}
        helperText={episodeIdleError ?? t('behaviour.episodeIdleTimeout.helper')}
      />
      <NumberField
        label={scopedLabel(idleGraceLabel, idleGraceMinutes)}
        ariaLabel={idleGraceLabel}
        description={withHint(
          t('behaviour.idleGrace.description'),
          idleGraceMinutes,
          'idleGraceMinutes',
        )}
        value={idleGraceMinutes.value}
        onChange={(value) => idleGraceMinutes.onChange(value ?? 30)}
        disabled={idleGraceMinutes.disabled}
        min={1}
        max={1440}
        suffix={t('units.minutes')}
        error={Boolean(idleGraceError)}
        helperText={idleGraceError ?? t('behaviour.idleGrace.helper')}
      />
      <SwitchField
        label={scopedLabel(quoteInboundLabel, quoteInboundInReplies)}
        ariaLabel={quoteInboundLabel}
        description={withHint(
          t('behaviour.quoteInbound.description'),
          quoteInboundInReplies,
          'quoteInboundInReplies',
        )}
        checked={quoteInboundInReplies.value}
        onChange={(value) => quoteInboundInReplies.onChange(value)}
        disabled={quoteInboundInReplies.disabled}
      />

      <Divider sx={{ my: 2 }} />
      <ButtonBase
        onClick={() => setAdvancedOpen((open) => !open)}
        aria-expanded={advancedOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          width: '100%',
          justifyContent: 'flex-start',
          py: 1,
        }}
      >
        <ExpandMoreRoundedIcon
          fontSize="small"
          sx={{
            transform: advancedOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 150ms',
          }}
        />
        <Box sx={{ textAlign: 'left' }}>
          <Typography variant="subtitle2">
            {advancedOpen ? t('behaviour.advanced.hide') : t('behaviour.advanced.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('behaviour.advanced.description')}
          </Typography>
        </Box>
      </ButtonBase>
      <Collapse in={advancedOpen}>
        <Stack sx={{ pt: 1 }}>
          <NumberField
            label={scopedLabel(identityNegativeCacheLabel, identityNegativeCacheTtlHours)}
            ariaLabel={identityNegativeCacheLabel}
            description={withHint(
              t('behaviour.identityNegativeCache.description'),
              identityNegativeCacheTtlHours,
              'identityNegativeCacheTtlHours',
            )}
            value={identityNegativeCacheTtlHours.value}
            onChange={(value) => identityNegativeCacheTtlHours.onChange(value ?? 24)}
            disabled={identityNegativeCacheTtlHours.disabled}
            min={1}
            max={168}
            suffix={t('units.hours')}
            error={Boolean(identityNegativeCacheError)}
            helperText={identityNegativeCacheError ?? t('behaviour.identityNegativeCache.helper')}
          />
          <NumberField
            label={scopedLabel(unsupportedCooldownLabel, unsupportedReplyCooldownSeconds)}
            ariaLabel={unsupportedCooldownLabel}
            description={withHint(
              t('behaviour.unsupportedCooldown.description'),
              unsupportedReplyCooldownSeconds,
              'unsupportedReplyCooldownSeconds',
            )}
            value={unsupportedReplyCooldownSeconds.value}
            onChange={(value) => unsupportedReplyCooldownSeconds.onChange(value ?? 60)}
            disabled={unsupportedReplyCooldownSeconds.disabled}
            min={0}
            max={3600}
            suffix={t('units.seconds')}
            error={Boolean(unsupportedCooldownError)}
            helperText={unsupportedCooldownError ?? t('behaviour.unsupportedCooldown.helper')}
          />
          <NumberField
            label={scopedLabel(deliveryMaxRedispatchLabel, deliveryMaxRedispatch)}
            ariaLabel={deliveryMaxRedispatchLabel}
            description={withHint(
              t('behaviour.deliveryMaxRedispatch.description'),
              deliveryMaxRedispatch,
              'deliveryMaxRedispatch',
            )}
            value={deliveryMaxRedispatch.value}
            onChange={(value) => deliveryMaxRedispatch.onChange(value ?? 5)}
            disabled={deliveryMaxRedispatch.disabled}
            min={0}
            max={20}
            suffix={t('units.attempts')}
            error={Boolean(deliveryMaxRedispatchError)}
            helperText={deliveryMaxRedispatchError ?? t('behaviour.deliveryMaxRedispatch.helper')}
          />
        </Stack>
      </Collapse>

      {activeChannel ? (
        <ResetScopeDialog
          open={confirmResetOpen}
          channel={activeChannel}
          onConfirm={() => {
            controller.resetCard();
            setConfirmResetOpen(false);
          }}
          onCancel={() => setConfirmResetOpen(false)}
        />
      ) : null}

      <Dialog open={confirmDefaultsOpen} onClose={() => setConfirmDefaultsOpen(false)}>
        <DialogTitle>
          {t('provenance.resetCardDialog.title', { card: t('behaviour.title') })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              resettableCount === 1
                ? 'provenance.resetCardDialog.body_one'
                : 'provenance.resetCardDialog.body_other',
              { count: resettableCount },
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDefaultsOpen(false)} autoFocus>
            {t('common:action.cancel')}
          </Button>
          <Button
            onClick={() => {
              controller.useCardDefaults();
              setConfirmDefaultsOpen(false);
            }}
          >
            {t('provenance.resetCardDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </SettingsCard>
  );
}
